/* eslint-disable camelcase */
import get from 'lodash/get'
import {
  RECEIVE_FIELDS_WITH_PRED_TECH_LOGS,
  RECEIVE_FIELDS_WEEKLY,
  RECEIVE_FIELDS_IDEAL_SEASON,
  RECEIVE_FIELDS_SEASON_MAX,
  RECEIVE_FIELDS_PRED_TECH_LAYERS_LOG
} from '../actions/predTech'
import { PRED_TECH_LOG_TYPES, PRED_TECH_ESTIMATION_TYPES } from '../constants'
// https://medium.datadriveninvestor.com/https-medium-com-sabesan96-why-you-should-choose-day-js-instead-of-moment-js-9cf7bb274bbd
import moment from 'moment'

const { SAC_HA, PROD_HA, ATR_PRED, NITRO, POL_PRED } = PRED_TECH_LOG_TYPES

const {
  HEMAV_PRODPH_ESTIMATION,
  HEMAV_ATR_ESTIMATION,
  HEMAV_POL_ESTIMATION,
  HEMAV_SAC_ESTIMATION,
  HEMAV_N_BUNCHES_ESTIMATION,
  HEMAV_NITROGEN_ESTIMATION
} = PRED_TECH_ESTIMATION_TYPES

const predTechInitialState = {
  idealParameters: false,

  [SAC_HA]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [PROD_HA]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [ATR_PRED]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [NITRO]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [POL_PRED]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [HEMAV_PRODPH_ESTIMATION]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [HEMAV_ATR_ESTIMATION]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [HEMAV_POL_ESTIMATION]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [HEMAV_SAC_ESTIMATION]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [HEMAV_N_BUNCHES_ESTIMATION]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  },
  [HEMAV_NITROGEN_ESTIMATION]: {
    idealCurve: [],
    idealMax: null,
    normalized: {}
  }
}

const normalizeData = forecasts => {
  return forecasts.reduce((acc, { field_id, log_date, week_avg, week_from_plantation_date, customer_name, field_name }) => {
    const date = moment(log_date, 'YYYY-MM-DD')
    const dateNumber = date.valueOf() // Convert date to timestamp
    const isFuture = date.isAfter(moment(), 'day')

    if (!acc[field_id]) {
      acc[field_id] = { byWeek: {}, byDate: {} }
    }

    const weekIndex = week_from_plantation_date.toString()
    const dataObject = { week_from_plantation_date, avg: week_avg, isFuture, date: dateNumber, customer_name, field_name }

    acc[field_id].byWeek[weekIndex] = dataObject
    acc[field_id].byDate[dateNumber] = dataObject

    return acc
  }, {})
}

const normalizeEstimationData = (forecasts, key) => {
  return forecasts.reduce((acc, forecast) => {
    const { field_id, forecast_execution_date, field_name } = forecast

    const date = moment(forecast_execution_date, 'YYYY-MM-DD')
    const dateNumber = date.valueOf()
    const isFuture = date.isAfter(moment(), 'day')

    const estimation = get(forecast, `hemav_${key}_estimation`)
    const avg = get(forecast, `hemav_${key}_estimation_avg`)
    const min = get(forecast, `hemav_${key}_estimation_min`)
    const max = get(forecast, `hemav_${key}_estimation_max`)

    if (!estimation) return acc

    if (!acc[field_id]) acc[field_id] = { byDate: {} }

    acc[field_id].byDate[dateNumber] = {
      date: dateNumber,
      estimation,
      avg,
      min,
      max,
      isFuture,
      field_name
    }

    return acc
  }, {})
}

export function predTech(state = predTechInitialState, { type, logType, mode, data }) {
  switch (type) {
    case RECEIVE_FIELDS_PRED_TECH_LAYERS_LOG: {
      const prodph = normalizeEstimationData(data, 'prodph')
      const atr = normalizeEstimationData(data, 'atr')
      const pol = normalizeEstimationData(data, 'pol')
      const sac = normalizeEstimationData(data, 'sac')
      const nBunches = normalizeEstimationData(data, 'n_bunches')
      const nitrogen = normalizeEstimationData(data, 'nitrogen')

      return {
        ...state,
        [HEMAV_PRODPH_ESTIMATION]: { ...state[HEMAV_PRODPH_ESTIMATION], idealParameters: false, normalized: prodph },
        [HEMAV_ATR_ESTIMATION]: { ...state[HEMAV_ATR_ESTIMATION], idealParameters: false, normalized: atr },
        [HEMAV_POL_ESTIMATION]: { ...state[HEMAV_POL_ESTIMATION], idealParameters: false, normalized: pol },
        [HEMAV_SAC_ESTIMATION]: { ...state[HEMAV_SAC_ESTIMATION], idealParameters: false, normalized: sac },
        [HEMAV_N_BUNCHES_ESTIMATION]: { ...state[HEMAV_N_BUNCHES_ESTIMATION], idealParameters: false, normalized: nBunches },
        [HEMAV_NITROGEN_ESTIMATION]: { ...state[HEMAV_NITROGEN_ESTIMATION], idealParameters: false, normalized: nitrogen }
      }
    }
    case RECEIVE_FIELDS_WITH_PRED_TECH_LOGS: {
      const { predictions, season_max_value, idealCurve } = data
      const modifiedArray = predictions && normalizeData(predictions)

      return season_max_value && idealCurve
        ? {
            ...state,
            idealParameters: true,
            [logType]: {
              ...state[logType],
              normalized: { ...modifiedArray },
              idealMax: season_max_value,
              idealCurve
            }
          }
        : {
            ...state,
            [logType]: {
              ...state[logType],
              normalized: { ...modifiedArray }
            }
          }
    }

    case RECEIVE_FIELDS_WEEKLY: {
      return {
        ...state,
        [logType]: {
          ...state[logType],
          normalized: { ...normalizeData(data) }
        }
      }
    }

    case RECEIVE_FIELDS_IDEAL_SEASON: {
      return {
        ...state,
        [logType]: {
          ...state[logType],
          idealCurve: data,
          idealParameters: true
        }
      }
    }

    case RECEIVE_FIELDS_SEASON_MAX: {
      return {
        ...state,
        [logType]: {
          ...state[logType],
          idealMax: data[0].value,
          idealParameters: true
        }
      }
    }

    default:
      return state
  }
}
